import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  CheckCircleFill,
  XCircleFill,
  Link45deg,
  BoxArrowInRight,
} from "react-bootstrap-icons";

import BG_URL from "../img/SG.png";
import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_CONFIRM_URL = `${API_BASE}/confirmVerifyUrl`;

const BRAND = "#3D74F5";
const BTN_STYLES = {
  primaryGrad: {
    background:
      "linear-gradient(135deg, rgba(61,116,245,1) 0%, rgba(31,87,225,1) 100%)",
    border: "none",
    boxShadow: "0 6px 18px rgba(61,116,245,0.35)",
  },
  outlineDark: {
    background: "transparent",
    color: "#1f2937",
    border: "1px solid rgba(0,0,0,0.25)",
  },
  ghostBrand: {
    background: "transparent",
    color: BRAND,
    border: `1px solid ${BRAND}`,
  },
  big: { height: 48, borderRadius: 14, fontWeight: 700, letterSpacing: 0.3 },
};

const looksLikeSuccess = (msg) =>
  /success|verified|already\s*verified/i.test(String(msg || ""));

export default function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const email = useMemo(
    () => (params.get("e") || params.get("email") || "").trim(),
    [params]
  );
  const emailCode = useMemo(
    () => (params.get("c") || params.get("email_code") || "").trim(),
    [params]
  );
  const encoded= useMemo(
    () => (params.get("ec") || params.get("encoded") || "").trim(),
    [params]
  );

  const [status, setStatus] = useState("idle"); 
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(15);

  useEffect(() => {
    const verifyByLink = async () => {
      if (!email || !emailCode) {
        setStatus("error");
        setMessage("Invalid verification link (missing email or code).");
        return;
      }

      try {
        setStatus("loading");
        setMessage("");

        const token = localStorage.getItem("authToken");
        const { data } = await axios.post(
          ENDPOINT_CONFIRM_URL,
          { email, email_code: emailCode, ec: encoded},
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            timeout: 20000,
          }
        );

        const serverMsg =
          data?.message ||
          "Your account has been successfully verified! Please kindly login.";
        if (data?.success === true || looksLikeSuccess(serverMsg)) {
          setStatus("success");
          setMessage(serverMsg);
        } else {
          setStatus("error");
          setMessage(
            serverMsg ||
              "Cannot verify at this moment. The URL may be invalid or expired."
          );
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          "Server error. Please try again later.";
        if (looksLikeSuccess(msg)) {
          setStatus("success");
          setMessage(
            msg ||
              "Your account has been successfully verified! Please kindly login."
          );
        } else {
          setStatus("error");
          setMessage(msg);
        }
      }
    };

    verifyByLink();
  }, [email, emailCode, encoded]);

  useEffect(() => {
    if (status !== "success") return;
    setCountdown(15);
    const t = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(t);
          navigate("/");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [status, navigate]);

  const retry = () => window.location.reload();
  const onGoLogin = () => navigate("/");

  const isLoading = status === "loading";
  const isSuccess = status === "success";
  const isError = status === "error";

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        paddingTop: "70px",
        paddingBottom: "40px",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.10) 35%, rgba(255,255,255,0.30) 100%)",
        }}
      />

      <Container style={{ zIndex: 1 }}>
        <Row className="justify-content-center">
          <Col xs={12} className="text-center">
            <img
              src={LOGO_URL}
              alt="TMIS Logo"
              style={{
                height: 90,
                objectFit: "contain",
                marginBottom: 18,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          </Col>

          <Col xs={12} md={10} lg={8} xl={6} xxl={5}>
            <Card className="register-card mx-auto custom-font">
              <Card.Body style={{ padding: 24 }}>
                <h3
                  className="fw-bold text-center"
                  style={{ letterSpacing: 1.1, marginBottom: 10, fontSize: 24 }}
                >
                  Email Verification
                </h3>

                <p className="text-center" style={{ marginBottom: 18 }}>
                  {email ? (
                    <>Verifying account for <strong>{email}</strong>…</>
                  ) : (
                    "Verifying account…"
                  )}
                </p>

                {/* Loading */}
                {isLoading && (
                  <div className="text-center my-3">
                    <Spinner animation="border" role="status" />
                    <div className="mt-3 fw-semibold">Verifying your account…</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Please wait while we confirm your verification link.
                    </div>
                  </div>
                )}

                {/* Success */}
                {isSuccess && (
                  <>
                    <div className="text-center my-3">
                      <CheckCircleFill size={56} color="#28a745" />
                    </div>
                    <Alert variant="success" className="text-center fw-semibold">
                      {message ||
                        "Your account has been successfully verified! Please kindly login."}
                    </Alert>
                    <div className="text-center mt-2">
                      Redirecting to login in <strong>{countdown}</strong>{" "}
                      second{countdown === 1 ? "" : "s"}…
                    </div>
                    <div className="d-grid mt-3">
                      <Button
                        onClick={onGoLogin}
                        style={{ ...BTN_STYLES.primaryGrad, ...BTN_STYLES.big }}
                      >
                        Go to Login Now
                      </Button>
                    </div>
                  </>
                )}

                {/* Error */}
                {isError && (
                  <>
                    <div className="text-center my-3">
                      <XCircleFill size={48} color="#dc3545" />
                    </div>
                    <Alert variant="danger" className="text-center fw-semibold">
                      {message || "Verification failed."}
                    </Alert>
                    <div className="d-grid gap-2 mt-3">
                      <Button
                        onClick={retry}
                        style={{ ...BTN_STYLES.ghostBrand, ...BTN_STYLES.big }}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <Link45deg size={18} />
                          Retry verification
                        </div>
                      </Button>
                      <Button
                        type="button"
                        onClick={onGoLogin}
                        style={{ ...BTN_STYLES.outlineDark, ...BTN_STYLES.big }}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <BoxArrowInRight size={18} />
                          Back to login
                        </div>
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
